import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { DataSidebar, SidebarItem } from "./data";
import StackKit from "@/shared/components/kit/stack";
import AvatarUploader from "@/shared/components/common/avatar";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import { fetchProfile } from "@/api/user/profile";
import { useQuery } from "@tanstack/react-query";
/**
 * Sidebar
 *
 * کامپوننت سایدبار برای داشبورد کاربر با قابلیت:
 * - نمایش آواتار کاربر
 * - منوی اصلی و زیرمنوهای ناوبری
 * - مدیریت وضعیت فعال و باز شدن آیتم‌ها
 * - انیمیشن گسترش و جمع شدن سایدبار
 * - امکان خروج از حساب کاربری
 *
 * ویژگی‌ها:
 * - تغییر مسیر با استفاده از react-router
 * - فراخوانی اطلاعات پروفایل با react-query
 * - ذخیره وضعیت باز بودن آیتم‌ها و آیتم فعال
 * - انیمیشن smooth هنگام گسترش/جمع شدن سایدبار و زیرمنوها
 * - دکمه خروج با پاک کردن توکن‌ها و localStorage
 *
 * استفاده:
 * @returns JSX.Element
 */

const Sidebar = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [activeParentId, setActiveParentId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleItem = (id: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleClick = (item: SidebarItem) => {
    setActiveItemId(item.id);
    setActiveParentId(item.id);
    toggleItem(item.id);

    if (item.children?.length > 0) {
      setActiveItemId(item.children[0].id);
    } else {
      navigate(item.path);
    }
  };

  useEffect(() => {
    DataSidebar.forEach((item) => {
      if (item.path === location.pathname) {
        setActiveItemId(item.id);
        setActiveParentId(item.id);
        setOpenItems((prev) => ({
          ...prev,
          [item.id]: true,
        }));
      }

      item.children?.forEach((child) => {
        if (child.path === location.pathname) {
          setActiveItemId(child.id);
          setActiveParentId(item.id);
          setOpenItems((prev) => ({
            ...prev,
            [item.id]: true,
          }));
        }
      });
    });
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("userPhone");
    localStorage.removeItem("ID");
    localStorage.removeItem("newrole");
    localStorage.removeItem("roleEnter");
    localStorage.removeItem("token");
    localStorage.removeItem("tokenbest");
    localStorage.removeItem("refreshToken");
    navigate("/login");
  };

  return (
    <div style={{ display: "flex", width: "225px" }}>
      <StackKit
        direction="column"
        sx={{
          bgcolor: "#EEEEEE",
          width: "225px",

          maxHeight: "100svh",
          minHeight: "100svh",
          position: "fixed",
          zIndex: 1000,
          borderLeft: "2px solid transparent",
          borderImage:
            "linear-gradient(180deg, rgba(0, 0, 0, 0) 5.37%, #2F2F2F 22.61%, rgba(0, 0, 0, 0) 53.61%) 1",
          color: "#136A74",
          padding: "10px",
          alignItems: "center",
        }}
        justifyContent="space-between"
      >
        <StackKit gap={2}>
          {/* Avatar */}
          <StackKit
            direction="row"
            sx={{
              width: "100%",
              alignItems: "center",
              borderBottom: "2px solid #4E4E4E",
              padding: "20px 10px",
            }}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <AvatarUploader isExpanded={isExpanded} />
          </StackKit>

          {/* Menu Items */}
          <StackKit direction="column" sx={{ width: "100%", marginTop: 0 }}>
            {DataSidebar.map((item) => (
              <div key={item.id} style={{ width: "100%" }}>
                <div
                  onClick={() => handleClick(item)}
                  style={{
                    padding: "10px",
                    backgroundColor:
                      activeParentId === item.id ? "#136A74" : "transparent",
                    color: activeParentId === item.id ? "#fff" : "#136A74",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isExpanded ? "flex-start" : "center",
                    gap: isExpanded ? "8px" : "0",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  <span>{item.icon}</span>
                  <span
                    style={{
                      opacity: 1,
                      transform: "translateX(0)",
                      transition: "opacity 0.3s ease, transform 0.3s ease",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      display: "inline-block",
                      minWidth: "100px",
                    }}
                  >
                    {item.title}
                  </span>
                </div>

                {item.children?.length > 0 && openItems[item.id] && (
                  <div style={{ paddingLeft: "20px" }}>
                    {item.children.map((child) => (
                      <div
                        key={child.id}
                        onClick={() => {
                          setActiveItemId(child.id);
                          navigate(child.path);
                        }}
                        style={{
                          padding: "6px 10px",
                          color:
                            activeItemId === child.id ? "#136a74" : "#949494",
                          backgroundColor: "transparent",
                          borderRadius: "6px",
                          marginTop: "4px",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          textAlign: "right",
                          fontSize: "12px",
                        }}
                      >
                        {child.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </StackKit>
        </StackKit>

        {/* Logout */}
        <div
          onClick={handleLogout}
          style={{
            color: "red",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            justifyContent: "center",
            cursor: "pointer",
            width: "100%",
            padding: "10px",
          }}
        >
          <LoginOutlinedIcon />
          <span>خروج</span>
        </div>
      </StackKit>
    </div>
  );
};

export default Sidebar;
